import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, BACKEND_URL } from '../config';
import { ErrorState } from '../components/ErrorState';
import { classifyError } from '../lib/apiError';
import {
  Tv, Check, Clock, AlertTriangle, Activity, BarChart3,
  PieChart, Lightbulb, BellRing, Monitor, Download
} from "lucide-react";

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Transaction Item Component
const TransactionItem = ({ transaction, showDownload = false }) => {
  const getStatusColor = (status) => {
    if (status === "completed") return "text-emerald-500";
    if (status === "pending") return "text-amber-500";
    return "text-red-500";
  };

  return (
    <div className="flex items-center justify-between p-3 border-b border-border/30 last:border-0">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          transaction.provider_code === "DSTV" ? "bg-purple-500/20" : "bg-green-500/20"
        }`}>
          <Tv className={`w-4 h-4 ${transaction.provider_code === "DSTV" ? "text-purple-500" : "text-green-500"}`} />
        </div>
        <div>
          <p className="font-medium text-sm">{transaction.package_name || transaction.provider_code}</p>
          <p className="text-xs text-muted-foreground">{new Date(transaction.created_at).toLocaleDateString()}</p>
        </div>
      </div>
      <div className="text-right flex items-center gap-3">
        <div>
          <p className="font-semibold">MWK {transaction.amount?.toLocaleString()}</p>
          <p className={`text-xs ${getStatusColor(transaction.status)}`}>{transaction.status}</p>
        </div>
        {showDownload && transaction.token && (
          <button className="p-2 hover:bg-muted rounded-lg" title="Download Receipt">
            <Download className="w-4 h-4 text-muted-foreground" />
          </button>
        )}
      </div>
    </div>
  );
};

export default function SubscriptionDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountHistory, setAccountHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchSubscriptions(); }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/subscriptions/dashboard");
      setData(response.data);
      if (response.data.subscriptions?.length > 0) {
        loadAccountHistory(response.data.subscriptions[0]);
      }
    } catch (err) {
      console.error("Failed to load subscriptions:", err);
      setError(classifyError(err));
    } finally {
      setLoading(false);
    }
  };

  const loadAccountHistory = async (subscription) => {
    setSelectedAccount(subscription);
    setHistoryLoading(true);
    try {
      const response = await api.get(`/subscriptions/${subscription.account_number}/history`);
      setAccountHistory(response.data);
    } catch (err) {
      toast.error(`Could not load subscription history: ${classifyError(err).message}`);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === "active") return "bg-emerald-500/20 text-emerald-500";
    if (status === "expiring_soon") return "bg-orange-500/20 text-orange-500";
    return "bg-red-500/20 text-red-500";
  };

  const getStatusIcon = (status) => {
    if (status === "active") return <Check className="w-4 h-4" />;
    if (status === "expiring_soon") return <Clock className="w-4 h-4" />;
    return <AlertTriangle className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 loading-shimmer rounded" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-32 loading-shimmer rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchSubscriptions} />;
  }

  if (!data || data.subscriptions.length === 0) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Tv className="w-6 h-6 text-blue-500" /> Keep the shows on
        </h1>
        <div className="text-center py-16 bg-card border border-border/50 rounded-2xl">
          <Tv className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
          <h3 className="text-lg font-semibold mb-2">No Subscriptions Found</h3>
          <p className="text-muted-foreground mb-4">Add DStv or GOtv so we can nudge you before the screen goes dark.</p>
          <button onClick={() => navigate("/buy?provider=DSTV")} className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-all" data-testid="buy-dstv-btn">
            Buy DStv Package
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="subscription-dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Tv className="w-6 h-6 text-blue-500" /> Keep the shows on
          </h1>
          <p className="text-muted-foreground">Never miss kickoff — we'll remind you before packages expire.</p>
        </div>
      </div>

      {/* Expiring Soon Alert */}
      {data.expiring_soon?.length > 0 && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4" data-testid="expiring-subscriptions-alert">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <BellRing className="w-5 h-5 text-orange-500" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-orange-500">Subscriptions Expiring Soon</h3>
              <div className="mt-2 space-y-2">
                {data.expiring_soon.map((sub, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <Tv className="w-4 h-4 text-blue-500" />
                      {sub.provider_code} - {sub.account_number}
                    </span>
                    <span className={`font-medium ${sub.days_left <= 1 ? "text-red-500" : "text-orange-500"}`}>
                      {sub.days_left === 0 ? "Expires today!" : `${sub.days_left} days left`}
                    </span>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate("/buy?provider=DSTV")} className="mt-3 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-all">
                Renew Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border/50 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Tv className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Total Spent</p>
              <p className="text-2xl font-bold">MWK {data.total_spent.toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border/50 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Check className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">Active</p>
              <p className="text-2xl font-bold">{data.active_count}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border/50 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
              <Monitor className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">DStv</p>
              <p className="text-2xl font-bold">{data.provider_breakdown?.DSTV || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border/50 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
              <Tv className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">GOtv</p>
              <p className="text-2xl font-bold">{data.provider_breakdown?.GOTV || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Selector */}
      <div className="flex flex-wrap gap-3">
        {data.subscriptions.map((sub) => (
          <button key={`${sub.provider_code}_${sub.account_number}`} onClick={() => loadAccountHistory(sub)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
              selectedAccount?.account_number === sub.account_number
                ? "bg-blue-500/10 border-blue-500 text-blue-400"
                : "bg-card border-border hover:border-blue-500/50"
            }`} data-testid={`sub-select-${sub.account_number}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sub.provider_code === "DSTV" ? "bg-purple-500/20" : "bg-green-500/20"}`}>
              <Tv className={`w-5 h-5 ${sub.provider_code === "DSTV" ? "text-purple-500" : "text-green-500"}`} />
            </div>
            <div className="text-left">
              <p className="font-medium">{sub.nickname || sub.account_number}</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{sub.provider_code}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded ${getStatusColor(sub.status)}`}>
                  {sub.status === "active" ? `${sub.days_left}d left` : sub.status === "expiring_soon" ? `${sub.days_left}d left` : "Expired"}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Subscription Details */}
      {historyLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <div key={i} className="h-64 loading-shimmer rounded-xl" />)}
        </div>
      ) : accountHistory ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Current Status */}
          <div className="bg-card border border-border/50 rounded-xl p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-500" /> Current Status
            </h3>
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${getStatusColor(accountHistory.stats.current_status)}`}>
                <div className="flex items-center gap-2 mb-2">
                  {getStatusIcon(accountHistory.stats.current_status)}
                  <span className="font-semibold capitalize">{accountHistory.stats.current_status.replace("_", " ")}</span>
                </div>
                {accountHistory.stats.days_left > 0 && (
                  <p className="text-sm">{accountHistory.stats.days_left} days remaining</p>
                )}
              </div>
              
              {accountHistory.stats.current_package && (
                <div>
                  <p className="text-sm text-muted-foreground">Current Package</p>
                  <p className="font-semibold">{accountHistory.stats.current_package}</p>
                </div>
              )}
              
              {accountHistory.stats.expires_at && (
                <div>
                  <p className="text-sm text-muted-foreground">Expires On</p>
                  <p className="font-medium">{new Date(accountHistory.stats.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
                </div>
              )}
              
              <button onClick={() => navigate(`/buy?provider=${accountHistory.provider_code}`)} 
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all mt-4">
                Renew Subscription
              </button>
            </div>
          </div>

          {/* Statistics */}
          <div className="bg-card border border-border/50 rounded-xl p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" /> Statistics
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-border/30">
                <span className="text-muted-foreground">Total Spent</span>
                <span className="font-bold text-lg">MWK {accountHistory.stats.total_spent.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border/30">
                <span className="text-muted-foreground">Total Renewals</span>
                <span className="font-semibold">{accountHistory.stats.total_renewals}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border/30">
                <span className="text-muted-foreground">Most Used Package</span>
                <span className="font-semibold text-blue-400">{accountHistory.stats.most_used_package || "N/A"}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-muted-foreground">Avg Renewal</span>
                <span className="font-semibold">{accountHistory.stats.avg_renewal_days} days</span>
              </div>
              
              {accountHistory.renewal_recommendation && (
                <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-400 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    {accountHistory.renewal_recommendation}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Package Distribution */}
          <div className="bg-card border border-border/50 rounded-xl p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-emerald-500" /> Package History
            </h3>
            <div className="space-y-3">
              {Object.entries(accountHistory.stats.package_distribution || {}).map(([pkg, count], idx) => {
                const total = Object.values(accountHistory.stats.package_distribution).reduce((a, b) => a + b, 0);
                const percentage = (count / total) * 100;
                return (
                  <div key={idx} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>{pkg}</span>
                      <span className="text-muted-foreground">{count} time{count !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${percentage}%` }} transition={{ delay: idx * 0.1 }}
                        className="h-full bg-blue-500 rounded-full" />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Available Packages */}
            <h4 className="font-medium mt-6 mb-3 text-sm text-muted-foreground">Available Packages</h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {accountHistory.packages_available?.map((pkg, idx) => (
                <div key={idx} className="flex justify-between text-sm py-1.5 border-b border-border/20 last:border-0">
                  <span>{pkg.name}</span>
                  <span className="font-medium">MWK {pkg.price.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* Recent Transactions */}
      {accountHistory && (
        <div className="bg-card border border-border/50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Recent Renewals</h3>
            <button onClick={() => navigate("/history")} className="text-sm text-primary hover:underline">View All</button>
          </div>
          <div className="space-y-3">
            {accountHistory.transactions?.slice(0, 5).map((tx) => (
              <TransactionItem key={tx.id} transaction={tx} showDownload />
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}